import { NextRouter, useRouter } from 'next/router'
import { ParsedUrlQueryInput } from 'querystring'
import { useContext, useEffect } from 'react'
import styled from 'styled-components'

import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import {
  BaseGrid,
  BaseGridItem,
} from '@awaze/design-system.components.base-grid'
import { Button } from '@awaze/design-system.components.button'
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
} from '@awaze/design-system.components.drawer'
import { usePrevious, useWindowWidth } from '@awaze/design-system.utils.react'
import {
  FILTER_UPDATE_NIGHTS,
  FILTER_UPDATE_RANGE,
  NO_RESULTS,
  RESULTS_UPDATED,
  RESULTS_UPDATING,
  ResultsUpdatedProps,
  SearchContextStateType,
  getResults,
  useSearchContextDispatch,
  useSearchContextState,
} from '@awaze/mfe-common.contexts.search'
import {
  TmsContext,
  TmsContextType,
  isTmsReady,
  DataLayerEventType,
  DataLayerFactory,
} from '@awaze/mfe-common.contexts.tms'
import type {
  CommonSearchResult,
  ICommonAggregations,
  SearchCriteria,
  SearchFormCriteria,
} from '@awaze/mfe-common.types-and-mocks.site-search-service'

import { Box } from '@awaze/design-system.components.box'
import { Container } from '@awaze/design-system.components.container'
import NoResults from '@awaze/design-system.compositions.no-results'
import {
  SearchFormConfig,
  SearchFormFields,
} from '@awaze/design-system.compositions.search-form'
import { Toaster, useToaster } from '@awaze/design-system.compositions.toaster'
import { useGuestAuth } from '@awaze/mfe-common.contexts.guest-auth'
import { useIntlContext } from '@awaze/mfe-common.contexts.intl'
import {
  WishlistProvider,
  WishlistTrackEventType,
} from '@awaze/mfe-common.contexts.wishlist'
import { Regions } from '@awaze/mfe-common.types-and-mocks.one-search-client'
import { OptimizelyDecisionWithOutUserContext } from '@awaze/mfe-common.lib.optimizely'

import { useDataLayerEvent } from 'src/components/SearchForm/hooks/useDataLayerEvent'
import { getIsTablet, onDesktop } from 'src/components/utils/dimension'
import {
  CORRELATION_ID_COOKIE_NAME,
  OPTIMIZELY_EVENTS,
  OPTIMIZELY_FLAGS_ENUM,
} from 'src/constants'
import Filters from 'src/containers/Filters'
import SearchResults from 'src/containers/SearchResults'
import { useOptimizelyDecisionsContext } from 'src/context/optimizely-decisions-context'
import { useApplicationConfig } from 'src/lib/applicationConfig'
import { useDisplayContext } from 'src/lib/display/displayContext'
import { SearchResultsTranslations } from 'src/types'
import { dataLayerErrorHandler } from 'src/utils/dataLayerErrorHandler'
import { getCookie, searchCriteriaComparisonString } from 'src/utils/helpers'
import { trackClientSideOptimizelyEvent } from 'src/utils/optimizely/trackOptimizelyEvent'
import {
  onWishlistTrack,
  wishlistTmsUpdateDataLayer,
  createSearchNoResultsPayload,
} from './SearchLayout.utils'

export interface SearchLayoutProps {
  brand: Brand
  filterControls: {
    open: () => void
    close: () => void
    isOpen: boolean
  }
  defaultConfig: SearchFormConfig
  initialValues: SearchFormFields
  translations: SearchResultsTranslations
  experimentDecisions?: Record<string, OptimizelyDecisionWithOutUserContext>
  optimizelyUserId: string
  host: string
}

const ShowOnDesktop = styled(Container)`
  display: none;

  ${onDesktop} {
    display: initial;
  }
`

const HideOnDesktop = styled(Container)`
  display: initial;

  ${onDesktop} {
    display: none;
  }
`

export const SearchLayout: React.FC<SearchLayoutProps> = ({
  brand,
  filterControls: { open, close, isOpen },
  defaultConfig,
  initialValues,
  translations,
  experimentDecisions = {},
  optimizelyUserId,
  host,
}) => {
  const {
    total = 0,
    hasResults,
    loading,
    searchCriteria = {},
    aggregations,
    rhsLevel,
  } = useSearchContextState()
  const dispatch = useSearchContextDispatch()
  const searchContextState = useSearchContextState()
  const { displayMode } = useDisplayContext()
  const { clientHeader, contentfulLocale, currency } = useApplicationConfig()
  const { formatMessage } = useIntlContext()
  const router = useRouter()
  const tms = useContext(TmsContext)
  const width = useWindowWidth()
  const isTablet = getIsTablet(width)
  /** ------------------EXPERIMENTS------------------- **/
  const optiContext = useOptimizelyDecisionsContext()

  // GD-131: OneSearch V1 VS V2
  const gd131VariationKey = optiContext?.getExperimentVariationKey(
    OPTIMIZELY_FLAGS_ENUM.GD_128_ONESEARCH_V2,
  )
  const isInGd131 = gd131VariationKey === 'variant_a'

  /** -------------------------------------------------- **/

  const previousState = usePrevious(searchContextState)

  const parsedSearchCriteria = (
    stateSearchCriteria: SearchCriteria,
  ): ParsedUrlQueryInput => {
    const features = stateSearchCriteria.features?.join(',')
    const siteFeatures = stateSearchCriteria.siteFeatures?.join(',')
    const minPrice = stateSearchCriteria.minPrice
    const maxPrice = stateSearchCriteria.maxPrice

    return {
      ...stateSearchCriteria,
      features: features !== '' ? features : [],
      siteFeatures: siteFeatures !== '' ? siteFeatures : [],
      bathrooms: stateSearchCriteria.bathrooms
        ? stateSearchCriteria.bathrooms
        : [],
      bedrooms: stateSearchCriteria.bedrooms
        ? stateSearchCriteria.bedrooms
        : [],
      grade: stateSearchCriteria.grade ? stateSearchCriteria.grade : [],
      rating: stateSearchCriteria.rating ? stateSearchCriteria.rating : [],
      ...(minPrice && { minPrice }),
      ...(maxPrice && { maxPrice }),
    }
  }

  const updateQueryParam = (
    router: NextRouter,
    stateSearchCriteria: SearchFormCriteria,
  ): void => {
    // AGE-199 - nextjs catch-all routes adds path params to query params
    // e.g. /england/ will add '?index=england' to the URL
    // this is undesirable for us, so we remove those params

    const query = parsedSearchCriteria(stateSearchCriteria)

    const sanitisedParams: ParsedUrlQueryInput = {
      ...query,
    }

    delete sanitisedParams.index
    delete sanitisedParams.regionUrl

    if (sanitisedParams.minPrice === null) delete sanitisedParams.minPrice
    if (sanitisedParams.maxPrice === null) delete sanitisedParams.maxPrice

    router.replace(
      {
        pathname: window.location.pathname,
        query: sanitisedParams,
      },
      undefined,
      { shallow: true },
    )
  }

  const fireDataLayerEvents = (
    state: SearchContextStateType,
    _searchCriteria: SearchCriteria,
    tms: TmsContextType,
    searchResults: CommonSearchResult,
  ): void => {
    const stateSearchCriteria = state.searchCriteria ?? {}
    const getPreviousPage = (
      previousState: SearchContextStateType | undefined,
    ): number => {
      return (
        previousState?.searchCriteria?.page ||
        getCurrentPage(stateSearchCriteria)
      )
    }

    const getCurrentPage = (searchCriteriaState: SearchCriteria): number => {
      return searchCriteriaState.page || 1
    }

    new DataLayerFactory().getEvent(DataLayerEventType.SearchPageView).update(
      tms,
      {
        searchCriteria: stateSearchCriteria,
        searchData: searchResults,
      },
      dataLayerErrorHandler,
    )

    const currentPage = getCurrentPage(stateSearchCriteria)
    const previousPage = getPreviousPage(previousState)

    if (searchResults?.properties?.length && previousPage !== currentPage) {
      new DataLayerFactory()
        .getEvent(DataLayerEventType.PaginationClick)
        .update(tms, { pageNumber: currentPage }, dataLayerErrorHandler)
    }
  }

  const searchCriteriaUpdated = searchCriteriaComparisonString(searchCriteria)

  const tmsReady = isTmsReady(tms)

  useEffect(() => {
    const shouldTriggerDataLayerPush =
      tmsReady && Object.keys(experimentDecisions).length > 0

    typeof newrelic !== 'undefined' &&
      tmsReady &&
      Object.keys(experimentDecisions).length === 0 &&
      newrelic.addPageAction('Optimizely-LogDecisions-NoDecisions', {
        userId: optimizelyUserId,
      })

    if (shouldTriggerDataLayerPush) {
      typeof newrelic !== 'undefined' &&
        newrelic.addPageAction('Optimizely-LogDecisions-Trigger', {
          userId: optimizelyUserId,
        })

      Object.keys(experimentDecisions).forEach((experimentDecision) => {
        new DataLayerFactory()
          .getEvent(DataLayerEventType.ExperienceImpression)
          .update(
            tms,
            {
              experiment_origin: 'flag',
              experiment_decision: experimentDecisions[experimentDecision],
            },
            dataLayerErrorHandler,
          )
      })
    }
  }, [tmsReady])

  useEffect(() => {
    dispatch({ type: RESULTS_UPDATING })
    const isMapMode = displayMode === 'MAP'
    const mapEndpoint = '/api/search/properties/coordinates'
    const listEndpoint = '/api/search/properties/list'

    const endpoint = isMapMode ? mapEndpoint : listEndpoint
    //todo replace with optimisely call

    const additionalQueryParams = {
      searchEngineVersion: isInGd131 ? 'v2' : 'v1',
    }

    getResults(searchCriteria, brand, endpoint, additionalQueryParams, {
      headers: {
        'x-awaze-client': clientHeader,
        'x-awaze-currency': currency,
        'x-awaze-locale': contentfulLocale,
      },
    })
      .then((results) => {
        const resultsWithProperties = 'properties' in results
        const resultsWithCoordinates = 'coordinates' in results
        const filteredCoordinates = resultsWithCoordinates
          ? results.coordinates.filter(
              ({ lat, lng }) => lat !== -999 && lng !== -999,
            )
          : []

        if (resultsWithProperties) {
          fireDataLayerEvents(searchContextState, searchCriteria, tms, results)
        }

        const completeResults: ResultsUpdatedProps = {
          coordinates: [],
          properties: [],
          rhsLevel: 'rhs1', // HACK: We do this also in Filters.tsx
          ...results,
          ...(resultsWithCoordinates && {
            coordinates: filteredCoordinates,
          }),
        }

        dispatch({
          type: RESULTS_UPDATED,
          results: completeResults,
        })
      })
      .catch((error) => {
        if (typeof newrelic !== 'undefined') {
          const correlationId = getCookie(CORRELATION_ID_COOKIE_NAME)
          newrelic.noticeError(
            error,
            correlationId ? { correlationId } : undefined,
          )
        }

        const searchNoResultsPayload =
          createSearchNoResultsPayload(searchCriteria)

        new DataLayerFactory()
          .getEvent(DataLayerEventType.SearchNoResults)
          .update(tms, searchNoResultsPayload, dataLayerErrorHandler)

        dispatch({
          type: NO_RESULTS,
        })
      })
      .finally(() => {
        updateQueryParam(router, searchCriteria)
      })
  }, [searchCriteriaUpdated, displayMode])

  useDataLayerEvent({ isOpen: isOpen, tms: tms })

  const showNoResults = !hasResults && !loading && displayMode === 'LIST'

  if (showNoResults) {
    trackClientSideOptimizelyEvent(OPTIMIZELY_EVENTS.NO_RESULTS, optiContext)
  }

  // HACK: AGE-2286 replacing first action to clear persisting filters
  const noResultsMessage = formatMessage({
    id: 'search-results.no-results.message',
    defaultMessage:
      'Unfortunately there were no results for your search, please try selecting a different date or duration.',
  })

  const firstActionLabel = formatMessage(
    {
      id: 'search-results.no-results.change-duration',
      defaultMessage: 'Change duration to 14 nights',
    },
    {
      nights: 14,
    },
  )

  const { add } = useToaster()

  const onWishlistError = (
    error: unknown,
    context: string,
  ): void | undefined => {
    if (typeof newrelic !== 'undefined') {
      newrelic.noticeError(error as Error, { context })
    }
  }
  return (
    <Container>
      {showNoResults ? (
        <BaseGrid>
          <BaseGridItem columns={12}>
            <Box
              radius={0}
              padding={[
                [60, 0],
                [60, 0],
                [60, 0],
              ]}
            >
              <NoResults
                onFirstActionClick={() => {
                  dispatch({ type: FILTER_UPDATE_NIGHTS, nights: 14 })
                }}
                onSecondActionClick={() =>
                  dispatch({ type: FILTER_UPDATE_RANGE, range: 3 })
                }
                translations={{
                  noResultsMessage: noResultsMessage,
                  actionsHeading: formatMessage({
                    id: 'search-results.no-results.action-heading',
                    defaultMessage: 'Get more results',
                  }),
                  firstActionLabel: firstActionLabel,
                  secondActionLabel: formatMessage(
                    {
                      id: 'search-results.no-results.change-flexibility',
                      defaultMessage: 'Change flexibility to +/-3 days',
                    },
                    {
                      range: 3,
                    },
                  ),
                }}
              />
            </Box>
          </BaseGridItem>
        </BaseGrid>
      ) : (
        <BaseGrid flush={[false, false, true]}>
          <BaseGridItem columns={[0, 0, 3]}>
            <ShowOnDesktop>
              <Filters />
            </ShowOnDesktop>
          </BaseGridItem>

          <BaseGridItem columns={[12, 12, isTablet ? 12 : 9]}>
            <WishlistProvider
              onToast={(toast) => {
                add(toast as unknown as Parameters<typeof add>[0])
              }}
              onError={onWishlistError}
              onTrack={(
                event: WishlistTrackEventType,
                payload?: Record<string, unknown>,
              ) => {
                if (
                  event === 'wishlist_undo_remove' ||
                  event === 'wishlist_add_failed' ||
                  event === 'wishlist_remove_failed'
                ) {
                  wishlistTmsUpdateDataLayer(tms, event, payload)
                  return
                }

                payload?.propertyId &&
                  onWishlistTrack({
                    tms,
                    eventName: event,
                    clientHeader,
                    currency,
                    contentfulLocale,
                    propertyInfoArgs: {
                      brand,
                      oneSearchVersion: isInGd131 ? 'v2' : 'v1',
                      propertyId: payload?.propertyId.toString(),
                      searchCriteria,
                      ...(searchCriteria.salesMarket && {
                        salesMarket: searchCriteria.salesMarket,
                      }),
                    },
                    propertyImpression: {
                      aggregations: aggregations as ICommonAggregations,
                      rhsLevel: rhsLevel as Regions,
                      total,
                    },
                  })
              }}
              useGuestAuth={useGuestAuth}
              useIntlContext={useIntlContext}
              hostname={host}
              silentQueryErrors
            >
              <SearchResults
                openFilters={open}
                defaultConfig={defaultConfig}
                initialValues={initialValues}
                translations={translations}
                isFilterDrawerOpen={isOpen}
              />
            </WishlistProvider>
            <Toaster position="bottom-center" />
            <HideOnDesktop>
              <Drawer isOpen={isOpen} placement="left" close={close}>
                <DrawerContent
                  header={formatMessage({
                    id: 'search-layout.filters',
                    defaultMessage: 'Filters',
                  })}
                  closeButtonTitle={formatMessage({
                    id: 'search-layout.close-filters.ariaLabel',
                    defaultMessage: 'Close filters',
                  })}
                >
                  <DrawerBody>
                    <Filters />
                  </DrawerBody>

                  <DrawerFooter>
                    <Button
                      text={
                        total > 1
                          ? formatMessage(
                              {
                                id: 'search-layout.show-results',
                                defaultMessage: 'Show {total} properties',
                              },
                              {
                                ...(!hasResults && displayMode === 'MAP'
                                  ? { total: 0 }
                                  : { total }),
                              },
                            )
                          : formatMessage({
                              id: 'search-layout.show-result',
                              defaultMessage: 'Show 1 property',
                            })
                      }
                      onClick={close}
                      colourScheme="brand-primary"
                      isFullWidth
                    />
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </HideOnDesktop>
          </BaseGridItem>
        </BaseGrid>
      )}
    </Container>
  )
}
